import { useState } from 'react'
import { X, Check, ChevronRight, Plus, Trash2 } from 'lucide-react'

/**
 * ColloquioForm — Form semplificato per nuovo/modifica colloquio
 *
 * Props:
 *   colloquio        - Oggetto colloquio esistente (null = nuovo)
 *   obiettiviAttivi  - Array obiettivi attivi del PEP (per checkbox)
 *   onConfirm        - callback(colloquioData)
 *   onClose          - callback per chiudere
 */

const TIPO_OPTIONS = [
  { value: 'colloquio_famiglia', label: 'Colloquio con la famiglia' },
  { value: 'colloquio_scuola', label: 'Colloquio con la scuola' },
  { value: 'equipe_servizi_sociali', label: 'Equipe servizi sociali' },
  { value: 'equipe_interna', label: 'Equipe interna' },
  { value: 'incontro_specialista', label: 'Incontro con specialista' },
  { value: 'udienza_tribunale', label: 'Udienza tribunale' },
  { value: 'visita_domiciliare', label: 'Visita domiciliare' },
  { value: 'telefonata', label: 'Telefonata' },
  { value: 'altro', label: 'Altro' }
]

const VISIBILITA_OPTIONS = [
  { value: 'tutti', label: 'Visibile a tutti' },
  { value: 'solo_coordinatore', label: 'Solo coordinatore' }
]

export default function ColloquioForm({ colloquio, obiettiviAttivi = [], onConfirm, onClose }) {
  const isEdit = !!colloquio

  const [dataEvento, setDataEvento] = useState(
    colloquio?.dataEvento ? toDateInputValue(new Date(colloquio.dataEvento)) : toDateInputValue(new Date())
  )
  const [tipo, setTipo] = useState(colloquio?.tipo || '')
  const [titolo, setTitolo] = useState(colloquio?.titolo || '')
  const [contenuto, setContenuto] = useState(colloquio?.contenuto || '')
  const [decisioni, setDecisioni] = useState(colloquio?.decisioni || '')
  const [prossimoIncontro, setProssimoIncontro] = useState(
    colloquio?.prossimoIncontro ? toDateInputValue(new Date(colloquio.prossimoIncontro)) : ''
  )
  const [visibilita, setVisibilita] = useState(colloquio?.visibilita || 'tutti')

  // Sezione espandibile
  const [showExtra, setShowExtra] = useState(false)
  const [partecipanti, setPartecipanti] = useState(
    colloquio?.partecipanti?.length > 0
      ? colloquio.partecipanti.map(p => ({ nome: p.nome || '', ruolo: p.ruolo || '' }))
      : []
  )
  const [obiettiviSelezionati, setObiettiviSelezionati] = useState(
    new Set(colloquio?.obiettiviCollegati?.map(id => String(id)) || [])
  )

  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const validate = () => {
    const errs = {}
    if (!dataEvento) errs.dataEvento = 'La data è obbligatoria'
    if (!tipo) errs.tipo = 'Il tipo è obbligatorio'
    if (!titolo.trim()) errs.titolo = 'Il titolo è obbligatorio'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setSubmitting(true)
    try {
      const data = {
        dataEvento: new Date(dataEvento).toISOString(),
        tipo,
        titolo: titolo.trim(),
        visibilita
      }

      if (contenuto.trim()) data.contenuto = contenuto.trim()
      if (decisioni.trim()) data.decisioni = decisioni.trim()
      if (prossimoIncontro) data.prossimoIncontro = new Date(prossimoIncontro).toISOString()

      // Partecipanti (filtra vuoti)
      const validPartecipanti = partecipanti.filter(p => p.nome.trim())
      if (validPartecipanti.length > 0) {
        data.partecipanti = validPartecipanti.map(p => ({
          nome: p.nome.trim(),
          ruolo: p.ruolo.trim() || undefined
        }))
      }

      // Obiettivi collegati
      if (obiettiviSelezionati.size > 0) {
        data.obiettiviCollegati = Array.from(obiettiviSelezionati)
      }

      await onConfirm(data)
    } catch {
      // errore gestito dal parent
    } finally {
      setSubmitting(false)
    }
  }

  // Partecipanti helpers
  const addPartecipante = () => {
    setPartecipanti(prev => [...prev, { nome: '', ruolo: '' }])
  }

  const updatePartecipante = (idx, field, value) => {
    setPartecipanti(prev => prev.map((p, i) =>
      i === idx ? { ...p, [field]: value } : p
    ))
  }

  const removePartecipante = (idx) => {
    setPartecipanti(prev => prev.filter((_, i) => i !== idx))
  }

  const toggleObiettivo = (obId) => {
    setObiettiviSelezionati(prev => {
      const next = new Set(prev)
      if (next.has(obId)) next.delete(obId)
      else next.add(obId)
      return next
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Modifica colloquio' : 'Nuovo colloquio'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Data + Tipo */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Data *
              </label>
              <input
                type="date"
                value={dataEvento}
                onChange={(e) => setDataEvento(e.target.value)}
                className={`form-input w-full ${errors.dataEvento ? 'border-red-500' : ''}`}
              />
              {errors.dataEvento && <p className="text-xs text-red-500 mt-1">{errors.dataEvento}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tipo *
              </label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                className={`form-input w-full ${errors.tipo ? 'border-red-500' : ''}`}
              >
                <option value="">Seleziona...</option>
                {TIPO_OPTIONS.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              {errors.tipo && <p className="text-xs text-red-500 mt-1">{errors.tipo}</p>}
            </div>
          </div>

          {/* Titolo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Titolo / Oggetto *
            </label>
            <input
              type="text"
              value={titolo}
              onChange={(e) => setTitolo(e.target.value)}
              placeholder="Es: Incontro con assistente sociale per aggiornamento PEI"
              className={`form-input w-full ${errors.titolo ? 'border-red-500' : ''}`}
              maxLength={200}
            />
            {errors.titolo && <p className="text-xs text-red-500 mt-1">{errors.titolo}</p>}
          </div>

          {/* Contenuto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Contenuto / Verbale
            </label>
            <textarea
              value={contenuto}
              onChange={(e) => setContenuto(e.target.value)}
              placeholder="Argomenti trattati, osservazioni emerse..."
              rows={4}
              className="form-input w-full resize-none"
              maxLength={5000}
            />
            <p className="text-xs text-gray-400 text-right mt-1">{contenuto.length}/5000</p>
          </div>

          {/* Decisioni */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Decisioni / Azioni concordate
            </label>
            <textarea
              value={decisioni}
              onChange={(e) => setDecisioni(e.target.value)}
              placeholder="Decisioni prese, azioni da intraprendere..."
              rows={3}
              className="form-input w-full resize-none"
              maxLength={2000}
            />
          </div>

          {/* Prossimo incontro + Visibilità */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Prossimo incontro
              </label>
              <input
                type="date"
                value={prossimoIncontro}
                onChange={(e) => setProssimoIncontro(e.target.value)}
                min={dataEvento || undefined}
                className="form-input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Visibilità
              </label>
              <select
                value={visibilita}
                onChange={(e) => setVisibilita(e.target.value)}
                className="form-input w-full"
              >
                {VISIBILITA_OPTIONS.map(v => (
                  <option key={v.value} value={v.value}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Sezione espandibile: Partecipanti + Obiettivi */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <button
              type="button"
              onClick={() => setShowExtra(!showExtra)}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              <ChevronRight className={`h-4 w-4 transition-transform ${showExtra ? 'rotate-90' : ''}`} />
              Partecipanti e obiettivi collegati
              <span className="text-xs text-gray-400 font-normal">(opzionale)</span>
            </button>

            {showExtra && (
              <div className="mt-3 space-y-4">
                {/* Partecipanti */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Partecipanti
                  </label>
                  <div className="space-y-2">
                    {partecipanti.map((p, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={p.nome}
                          onChange={(e) => updatePartecipante(idx, 'nome', e.target.value)}
                          placeholder="Nome"
                          className="form-input flex-1 text-sm py-1.5"
                          maxLength={100}
                        />
                        <input
                          type="text"
                          value={p.ruolo}
                          onChange={(e) => updatePartecipante(idx, 'ruolo', e.target.value)}
                          placeholder="Ruolo"
                          className="form-input w-40 text-sm py-1.5"
                          maxLength={100}
                        />
                        <button
                          onClick={() => removePartecipante(idx)}
                          className="text-red-400 hover:text-red-600 p-1"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addPartecipante}
                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 mt-2"
                  >
                    <Plus className="h-3 w-3" />
                    Aggiungi partecipante
                  </button>
                </div>

                {/* Obiettivi collegati */}
                {obiettiviAttivi.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                      Obiettivi collegati
                    </label>
                    <div className="space-y-1.5">
                      {obiettiviAttivi.map(ob => (
                        <label
                          key={ob._id}
                          className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={obiettiviSelezionati.has(String(ob._id))}
                            onChange={() => toggleObiettivo(String(ob._id))}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <div className="min-w-0">
                            <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{ob.titolo}</p>
                            <p className="text-[10px] text-gray-400">{ob.area}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Annulla
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-primary flex items-center"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Salvataggio...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" />
                {isEdit ? 'Salva modifiche' : 'Registra colloquio'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Helpers ─────────────────────────────────────────────────

function toDateInputValue(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
